import { Box, Typography, useTheme, IconButton, Tooltip as MuiTooltip, Button, Collapse } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { CartesianGrid, Label, Legend, ReferenceArea, ReferenceLine, ResponsiveContainer, Scatter, Cell, ScatterChart as SChart, Tooltip, XAxis, YAxis, ZAxis, Customized } from "recharts";
import DownloadComponentButton from "../button/DownloadComponentButton";
import { formatTo2Decimals, formatToK } from "../grid/columnDefinitions/columnDefinitions";
import { useGetColor } from "./Colors";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import { getOrgTokens } from "../../theme/style/organizationPalette";

/**
 *
 * @param {{
 * x: { type: "number" | "category", dataKey: string, name: string, unit:string, scale?: "linear" | "log" },
 * y: { type: "number" | "category", dataKey: string, name: string, unit:string, scale?: "linear" | "log" },
 * z: { type: "number" | "category", dataKey: string, name: string, unit:string } | null,
 * title: string,
 * data:[x_dataKey: number|string, y_dataKey: number|string, z_dataKey?: number|string] ,
 * referenceLines: [{ axis: "x" | "y", value: number, label?: string, dashed?:boolean }],
 * sizeKey?: string  // when set, bubble area scales with this field's value
 * }}
 * @returns
 */
const ScatterChart = ({
  x = { type: "number", dataKey: "x", name: "x_name", unit: "x_unit" },
  y = { type: "number", dataKey: "y", name: "y_name", unit: "y_unit" },
  z = { type: "number", dataKey: "z", name: "z_name", unit: "z_unit" },
  title = "",
  data: rawData = [
    { x: 100, y: 200 },
    { x: 120, y: 100 },
  ],
  referenceLines = [],
  labelKey = "label",
  hideLegend = false,
  singleSeries = false,
  groupBy = null,
  referenceAreas = [],
  onPointClick = null,
  height = 400,
  sizeKey = null,
  sizeRange = GRAPH_PALETTE.scatter.bubbleRadius,
}) => {
  const getColor = useGetColor();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const ref = useRef();
  const { t } = useTranslation();
  const [visibility, setVisibility] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);

  // Dynamically format numbers using 't' (tons) if the axis deals with Emissions (kg CO2e)
  const formatTick = useCallback((unit) => (val) => {
    const num = Number(val);
    if (!isNaN(num)) {
      if (num >= 1000) {
        if (unit && (unit.includes("kg") || unit.includes("CO₂e"))) {
          return `${(num / 1000).toFixed(1)} t`;
        }
        return `${(num / 1000).toFixed(1)}k`;
      }
      if (num % 1 !== 0) {
        return parseFloat(num.toFixed(2));
      }
      return num;
    }
    return val;
  }, []);

  const isLogX = x.scale === "log";
  const isLogY = y.scale === "log";

  // Log axes cannot show zero or negative values, so those points are left out.
  const data = useMemo(
    () =>
      (rawData || []).filter(
        (d) => (!isLogX || Number(d[x.dataKey]) > 0) && (!isLogY || Number(d[y.dataKey]) > 0)
      ),
    [rawData, isLogX, isLogY, x.dataKey, y.dataKey]
  );
  const excludedCount = (rawData || []).length - data.length;

  // Decade-aligned domain for log axes, widened by a fraction of a decade at both ends
  // so the largest bubbles are not cut off at the plot edge.
  const LOG_DOMAIN_PADDING_DECADES = 0.35;
  const getLogDomain = (arr, key) => {
    const vals = arr.map((d) => Number(d[key])).filter((v) => v > 0 && isFinite(v));
    if (!vals.length) return [0.001, 1];
    let lo = Math.floor(Math.log10(Math.min(...vals)));
    let hi = Math.ceil(Math.log10(Math.max(...vals)));
    if (lo === hi) hi += 1;
    return [Math.pow(10, lo - LOG_DOMAIN_PADDING_DECADES), Math.pow(10, hi + LOG_DOMAIN_PADDING_DECADES)];
  };

  // Ticks stay on whole powers of 10 that fall inside the padded domain.
  const getLogTicks = ([min, max]) => {
    const ticks = [];
    for (let e = Math.ceil(Math.log10(min)); e <= Math.floor(Math.log10(max)); e++) ticks.push(Math.pow(10, e));
    return ticks;
  };

  // Decade ticks: 0.01, 0.1, 1, 10, 100, 1k, 10k ...
  const formatLogTick = (val) => {
    const num = Number(val);
    if (num >= 1000) return `${Number((num / 1000).toPrecision(3)).toLocaleString()}k`;
    return num.toLocaleString(undefined, { maximumSignificantDigits: 3 });
  };

  // Bubble radius: area proportional to the size value (sqrt scale), clamped to the token range.
  const maxSizeValue = useMemo(() => {
    if (!sizeKey) return 0;
    return Math.max(0, ...data.map((d) => Math.abs(Number(d[sizeKey])) || 0));
  }, [data, sizeKey]);

  const getRadius = useCallback(
    (point) => {
      if (!sizeKey || maxSizeValue <= 0) return sizeRange.default;
      const value = Math.abs(Number(point?.[sizeKey])) || 0;
      return Math.max(sizeRange.min, sizeRange.max * Math.sqrt(value / maxSizeValue));
    },
    [sizeKey, maxSizeValue, sizeRange]
  );

  const getNumericDomain = (arr, key) => {
    const vals = arr.map((d) => Number(d[key])).filter((v) => !isNaN(v));
    if (!vals.length) return [0, 10];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    if (min === max) return [min === 0 ? 0 : min * 0.9, min === 0 ? 10 : min * 1.1];
    const range = max - min;
    const padding = range * 0.2;
    return [Math.max(0, min - padding), max + padding];
  };

  const xDomain = useMemo(() => (isLogX ? getLogDomain(data, x.dataKey) : getNumericDomain(data, x.dataKey)), [data, x.dataKey, isLogX]);
  const yDomain = useMemo(() => (isLogY ? getLogDomain(data, y.dataKey) : getNumericDomain(data, y.dataKey)), [data, y.dataKey, isLogY]);

  const autoLabelPosition = useCallback(
    (line) => {
      if (line.axis === "x") {
        const mid = (xDomain[0] + xDomain[1]) / 2;
        return line.value <= mid ? "right" : "left";
      }
      const mid = isLogY ? Math.sqrt(yDomain[0] * yDomain[1]) : (yDomain[0] + yDomain[1]) / 2;
      return line.value <= mid ? "top" : "bottom";
    },
    [xDomain, yDomain, isLogY]
  );

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: isDark ? "#1e293b" : "white",
          border: 1,
          borderColor: isDark ? "#334155" : "#ccc",
          borderRadius: 2,
          padding: 2,
          fontFamily: GRAPH_PALETTE.font.family,
          fontSize: 14,
          color: isDark ? "#f1f5f9" : "#333",
          pointerEvents: "none",
        }}>
        {point[labelKey] && <Box sx={{ fontWeight: 600, paddingBottom: 1, fontSize: 16 }}>{point[labelKey]}</Box>}
        {payload.map((entry, index) => (
          <Box key={index}>
            {entry.name}: {formatTo2Decimals(entry.value)} {entry.unit || ""}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      onMouseEnter={() => setVisibility(true)}
      onMouseLeave={() => setVisibility(false)}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: "5px", right: "5px", display: "flex", gap: 1, zIndex: 10 }}>
        <DownloadComponentButton ref={ref} filename={t(title) + ".png"} visibility={visibility} />
      </Box>
      <Box ref={ref} sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column", backgroundColor: tok.bg.card }}>
        {title && (
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            px: 3, 
            py: 2, 
            borderBottom: 1, 
            borderColor: tok.border.divider 
          }}>
            <Typography variant="Chart Title" sx={{ color: tok.text.primary, fontWeight: 600 }}>
              {t(title)}
            </Typography>
          </Box>
        )}
        <Box sx={{ flexGrow: 1, position: "relative", px: 2, pb: 2, minHeight: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <SChart margin={{ top: 30, right: 30, bottom: 50, left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"} />
                <XAxis
                  type={x.type}
                  dataKey={x.dataKey}
                  name={t(x.name)}
                  domain={xDomain}
                  {...(isLogX ? { scale: "log", ticks: getLogTicks(xDomain), allowDataOverflow: true } : {})}
                  tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12, fill: theme.palette.text.secondary }}
                  tickFormatter={isLogX ? formatLogTick : formatTick(x.unit)}
                  axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
                  tickLine={false}
                >
                  <Label
                    value={`${t(x.name)}${x.unit ? ` (${x.unit})` : ""}`}
                    position="bottom"
                    offset={25}
                    style={{
                      fill: theme.palette.text.primary,
                      fontFamily: GRAPH_PALETTE.font.family,
                      fontSize: parseInt(GRAPH_PALETTE.font.sizes.mainAxisTitle) || 13,
                      fontWeight: 600,
                      textAnchor: 'middle'
                    }}
                  />
                </XAxis>
                <YAxis
                  type={y.type}
                  dataKey={y.dataKey}
                  name={t(y.name)}
                  domain={yDomain}
                  {...(isLogY ? { scale: "log", ticks: getLogTicks(yDomain), allowDataOverflow: true } : {})}
                  tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12, fill: theme.palette.text.secondary }}
                  tickFormatter={isLogY ? formatLogTick : formatTick(y.unit)}
                  axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
                  tickLine={false}
                >
                  <Label
                    value={`${t(y.name)}${y.unit ? ` (${y.unit})` : ""}`}
                    position="left"
                    angle={-90}
                    offset={40}
                    style={{
                      fill: theme.palette.text.primary,
                      fontFamily: GRAPH_PALETTE.font.family,
                      fontSize: parseInt(GRAPH_PALETTE.font.sizes.mainAxisTitle) || 13,
                      fontWeight: 600,
                      textAnchor: 'middle'
                    }}
                  />
                </YAxis>
                <ZAxis
                  type={z.type}
                  dataKey={z.dataKey}
                  name={t(z.name)}
                  unit={" " + z.unit}
                  tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 14, fill: theme.palette.text.main }}
                  tickFormatter={formatTick(z.unit)}
                  range={[300, 700]}
                />
                {referenceAreas.map((area, idx) => (
                  <ReferenceArea
                    key={`ra-${idx}`}
                    x1={area.x1}
                    x2={area.x2}
                    y1={area.y1}
                    y2={area.y2}
                    fill={area.fill}
                    strokeOpacity={0}
                    fillOpacity={area.fillOpacity || 1}
                    ifOverflow="extendDomain"
                    style={{ pointerEvents: "none" }}
                  />
                ))}
                <Tooltip content={CustomTooltip} />
                {groupBy ? (() => {
                  const groups = Array.from(new Set(data.map(d => d[groupBy] || "Other")));
                  return groups.map((grpName, index) => {
                    const color = getColor(index, groups.length);
                    const grpData = data.filter(d => (d[groupBy] || "Other") === grpName);
                    return (
                      <Scatter
                        key={index}
                        name={grpName}
                        data={grpData}
                        shape={(props) => <circle cx={props.cx} cy={props.cy} r={getRadius(props.payload)} fill={color} stroke="#fff" strokeWidth={2} />}
                        fill={color}
                        onClick={(props) => onPointClick && onPointClick(props?.payload || props)}
                        cursor={onPointClick ? "pointer" : "default"}
                      />
                    );
                  });
                })() : singleSeries ? (
                  <Scatter
                    name="Data"
                    data={data}
                    shape={(props) => <circle cx={props.cx} cy={props.cy} r={getRadius(props.payload)} fill={getColor(props.index ?? 0, data.length)} stroke="#fff" strokeWidth={2} />}
                    onClick={(props) => onPointClick && onPointClick(props?.payload || props)}
                    cursor={onPointClick ? "pointer" : "default"}
                  >
                    {data.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={getColor(index, data.length)} />
                    ))}
                  </Scatter>
                ) : (
                  data.map((point, index) => {
                    const color = getColor(index, data.length);
                    return (
                      <Scatter
                        key={index}
                        name={point[labelKey] ?? `Point ${index + 1}`}
                        data={[point]}
                        shape={(props) => <circle cx={props.cx} cy={props.cy} r={getRadius(props.payload)} fill={color} stroke="#fff" strokeWidth={2} />}
                        fill={color}
                        onClick={(props) => onPointClick && onPointClick(props?.payload || props)}
                        cursor={onPointClick ? "pointer" : "default"}
                      />
                    );
                  })
                )}
                {referenceLines.map((line, idx) => (
                  <ReferenceLine
                    key={idx}
                    {...(line.axis === "y" ? { y: line.value } : { x: line.value })}
                    stroke={theme.palette.text.secondary}
                    strokeDasharray={line.dashed ? "6 6" : undefined}
                    ifOverflow="extendDomain"
                    label={
                      line.label
                        ? {
                          value: t(line.label),
                          position: autoLabelPosition(line),
                          fill: theme.palette.text.secondary,
                          fontFamily: GRAPH_PALETTE.font.family,
                          fontSize: 14,
                        }
                        : undefined
                    }
                    />
                ))}
              </SChart>
            </ResponsiveContainer>
        </Box>
        {excludedCount > 0 && (
          <Typography sx={{ px: 3, fontFamily: GRAPH_PALETTE.font.family, fontSize: tok.font.size.caption, color: tok.text.secondary, textAlign: "left" }}>
            {t("{{count}} item(s) with a zero value are not shown on the logarithmic scale", { count: excludedCount })}
          </Typography>
        )}

        {!hideLegend && (
          <Box sx={{ px: 3, pb: 1, flexShrink: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                size="small"
                onClick={() => setLegendOpen(!legendOpen)}
                startIcon={legendOpen ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  color: theme.palette.text.secondary,
                  fontFamily: GRAPH_PALETTE.font.family,
                  fontWeight: GRAPH_PALETTE.font.weights.standard,
                  fontSize: "14px",
                  "&:hover": { backgroundColor: "transparent", color: theme.palette.text.primary }
                }}
              >
                {legendOpen ? t("Hide Legend") : t("Show Legend")}
              </Button>
            </Box>
            <Collapse in={legendOpen} sx={{ maxHeight: 160, overflowY: "auto" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  columnGap: 2,
                  rowGap: 1.5,
                  mt: 2,
                  pt: 2,
                  px: 1,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                {(groupBy ? (() => {
                  const groups = Array.from(new Set(data.map(d => d[groupBy] || "Other")));
                  return groups.map((grpName, index) => ({
                    label: grpName,
                    color: getColor(index, groups.length)
                  }));
                })() : singleSeries ? (
                  [{ label: "Data", color: getColor(0, data.length) }]
                ) : (
                  data.map((point, index) => ({
                    label: point[labelKey] ?? `Point ${index + 1}`,
                    color: getColor(index, data.length)
                  }))
                )).map((item, idx) => (
                  <Box key={`legend-${idx}`} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: item.color, flexShrink: 0, mt: 0.5 }} />
                    <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard || "14px", lineHeight: 1.3, color: theme.palette.text.primary, fontWeight: GRAPH_PALETTE.font.weights.standard }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default ScatterChart;
